import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Ticket, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import InkPhDLogo from '@/components/InkPhDLogo';
import AppFooter from '@/components/AppFooter';
import SeoHead from '@/components/SeoHead';

const CODE = 'SCIINK30';

const PERKS = [
  'Your evidence library, your citations — every sentence retrieved from papers you added',
  'EQUATOR compliance checks for the design you actually used (CONSORT, STROBE, PRISMA, SPIRIT…)',
  'Defence Panel: five AI reviewers attack the manuscript before a real reviewer does',
  'Export to DOCX, PDF and LaTeX with Vancouver, APA 7, AMA 11 or BibTeX references',
];

export default function JournalAuthorsPage() {
  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-[#0a0e1a] text-foreground thesis-brand">
      <SeoHead title="InkPhD for journal authors" path="/journal-authors"
        description="Authors publishing with International Sci Ink Press get 30% off their first InkPhD payment with code SCIINK30." />
      <header className="border-b border-border">
        <div className="max-w-5xl mx-auto px-3 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
            data-testid="journal-back-link">
            <ArrowLeft className="w-4 h-4" /> Home
          </Link>
          <InkPhDLogo size="sm" />
        </div>
      </header>

      <main className="flex-1 w-full max-w-5xl mx-auto px-3 sm:px-6 py-12 sm:py-16"
        data-testid="journal-authors-page">
        <p className="text-[11px] uppercase tracking-widest text-[#1d4ed8] dark:text-[#7aa9ff] font-semibold mb-4">
          For authors publishing with International Sci Ink Press
        </p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-semibold tracking-tight mb-5 max-w-3xl"
          style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
          Write your next paper with the evidence already in your hands
        </h1>
        <p className="text-base text-muted-foreground max-w-2xl mb-8">
          If you have published — or are submitting — in one of our journals, InkPhD is the writing
          infrastructure built for exactly that workflow: your sources, verified numbered citations,
          reporting-guideline compliance and a defence rehearsal before submission.
        </p>

        <div className="rounded-2xl border border-[#1d4ed8]/40 bg-[#1d4ed8]/[0.05] p-6 mb-10
          flex flex-wrap items-center gap-6" data-testid="journal-code-card">
          <div>
            <p className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">
              Your author code
            </p>
            <p className="text-3xl font-mono font-semibold tracking-tight" data-testid="journal-code">
              {CODE}
            </p>
            <p className="text-[12px] text-muted-foreground mt-1">
              30% off your first payment on any plan · enter it in the Promo code field at checkout
            </p>
          </div>
          <div className="flex-1" />
          <div className="flex flex-wrap gap-3">
            <Button asChild className="bg-[#0b1a35] hover:bg-[#1d4ed8] text-white rounded-full"
              data-testid="journal-claim-btn">
              <Link to="/thesis/register">Start free, then apply the code <ArrowRight className="w-4 h-4 ml-2" /></Link>
            </Button>
            <Button asChild variant="outline" className="rounded-full" data-testid="journal-plans-btn">
              <Link to="/thesis/billing"><Ticket className="w-4 h-4 mr-2" />See plans</Link>
            </Button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <h2 className="text-base md:text-lg font-semibold mb-3">What you get</h2>
            <ul className="space-y-2">
              {PERKS.map((p) => (
                <li key={p} className="flex gap-2 text-[14px] leading-relaxed">
                  <Check className="w-4 h-4 mt-1 text-[#1d4ed8] shrink-0" /><span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="text-base md:text-lg font-semibold mb-3">How to use the code</h2>
            <ol className="space-y-2 text-[14px] leading-relaxed text-foreground/85">
              <li><strong>1.</strong> Create a free account — every new account starts a 14-day Doctoral trial, no card needed.</li>
              <li><strong>2.</strong> Try the demo project, then build your own with your PDFs and PubMed imports.</li>
              <li><strong>3.</strong> When you choose a plan, type <span className="font-mono">{CODE}</span> in the Promo code field and press Apply — the new price shows before you pay.</li>
            </ol>
            <p className="text-[12px] text-muted-foreground mt-4">
              Questions about eligibility? <Link to="/contact" className="underline">Contact us</Link> —
              mention the journal and the issue you published in.
            </p>
          </div>
        </div>
      </main>

      <AppFooter lang="en" />
    </div>
  );
}
